import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon, FileTextIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);
  const isPrivacy = type === 'privacy';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-124">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-125">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#2D3436]/10 flex-shrink-0" data-id="element-126">
          <div className="flex items-center gap-3" data-id="element-127">
            {isPrivacy ? <ShieldIcon className="w-5 h-5 text-[#5B8F7B]" data-id="element-128" /> : <FileTextIcon className="w-5 h-5 text-[#5B8F7B]" data-id="element-129" />}
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#2D3436]" data-id="element-130">
              {isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones'}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#2D3436]/5 transition-colors" aria-label="Cerrar" data-id="element-131">
            <XIcon className="w-5 h-5 text-[#2D3436]/60" data-id="element-132" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-6 text-[#2D3436]/80 text-sm leading-relaxed space-y-4" data-id="element-133">
          {isPrivacy ? <PrivacyContent data-id="element-134" /> : <TermsContent data-id="element-135" />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2D3436]/10 flex-shrink-0" data-id="element-136">
          <button onClick={onClose} className="w-full bg-[#5B8F7B] text-white font-medium py-2.5 rounded-full hover:bg-[#4A7A66] transition-colors text-sm" data-id="element-137">
            Entendido
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-[#2D3436]/50 text-xs" data-id="element-138">
        Última actualización: Marzo 2026
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-139">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-140">
        Al adquirir el Protocolo "Vientre Plano Post-30", recopilamos únicamente
        la información necesaria para procesar tu compra y entregarte el
        producto digital: tu nombre y dirección de correo electrónico.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-141">
        2. Uso de la Información
      </h3>
      <p data-id="element-142">Tu información personal se utiliza exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-143">
        <li data-id="element-144">Procesar y confirmar tu compra.</li>
        <li data-id="element-145">Enviarte el acceso al producto digital adquirido.</li>
        <li data-id="element-146">
          Comunicaciones relacionadas con tu compra o actualizaciones del
          producto.
        </li>
      </ul>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-147">
        3. Protección de Datos
      </h3>
      <p data-id="element-148">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado,
        alteración, divulgación o destrucción. Todos los pagos se procesan a
        través de plataformas seguras con encriptación SSL.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-149">
        4. Compartir Información
      </h3>
      <p data-id="element-150">
        No vendemos, intercambiamos ni transferimos tu información personal a
        terceros. La única excepción son los proveedores de servicios de pago
        que procesan tu transacción de forma segura.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-151">
        5. Cookies
      </h3>
      <p data-id="element-152">
        Este sitio puede utilizar cookies para mejorar tu experiencia de
        navegación. Las cookies no contienen información personal identificable.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-153">
        6. Tus Derechos
      </h3>
      <p data-id="element-154">
        Tienes derecho a acceder, rectificar o solicitar la eliminación de tus
        datos personales en cualquier momento. Para ejercer estos derechos,
        puedes comunicarte con nosotros a través de los canales disponibles.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-155">
        7. Cambios en esta Política
      </h3>
      <p data-id="element-156">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán publicados en esta misma página con
        la fecha de actualización correspondiente.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-[#2D3436]/50 text-xs" data-id="element-157">
        Última actualización: Marzo 2026
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-158">
        1. Descripción del Producto
      </h3>
      <p data-id="element-159">
        El Protocolo "Vientre Plano Post-30" es un producto digital educativo e
        informativo que incluye guías, planes de acción, técnicas de respiración
        y listas de alimentos. Se entrega en formato digital inmediatamente
        después de la compra.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-160">
        2. Naturaleza Informativa
      </h3>
      <p data-id="element-161">
        Este producto tiene fines exclusivamente educativos e informativos. No
        constituye consejo médico, diagnóstico ni tratamiento. Consulta siempre
        con un profesional de la salud antes de iniciar cualquier programa de
        bienestar o cambio en tu alimentación.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-162">
        3. Entrega del Producto
      </h3>
      <p data-id="element-163">
        Al completar tu compra, recibirás acceso inmediato al contenido digital
        a través del correo electrónico proporcionado. Es tu responsabilidad
        asegurarte de que la dirección de correo sea correcta.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-164">
        4. Política de Reembolso
      </h3>
      <p data-id="element-165">
        Debido a la naturaleza digital e inmediata del producto, todas las
        ventas son finales. Al realizar la compra, aceptas que estás adquiriendo
        un producto digital de acceso instantáneo.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-166">
        5. Propiedad Intelectual
      </h3>
      <p data-id="element-167">
        Todo el contenido del Protocolo "Vientre Plano Post-30" está protegido
        por derechos de autor. Queda prohibida la reproducción, distribución o
        reventa total o parcial del material sin autorización expresa por
        escrito.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-168">
        6. Resultados
      </h3>
      <p data-id="element-169">
        Los resultados mencionados son basados en experiencias reportadas y
        pueden variar de persona a persona. No garantizamos resultados
        específicos, ya que estos dependen de factores individuales como
        adherencia al protocolo, condición de salud y estilo de vida.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-170">
        7. Limitación de Responsabilidad
      </h3>
      <p data-id="element-171">
        No nos hacemos responsables de cualquier daño directo o indirecto
        derivado del uso o la incapacidad de usar el contenido de este producto.
        El uso del material es bajo tu propia responsabilidad.
      </p>

      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D3436] text-base pt-2" data-id="element-172">
        8. Aceptación de Términos
      </h3>
      <p data-id="element-173">
        Al realizar tu compra, confirmas que has leído, entendido y aceptado
        estos términos y condiciones en su totalidad.
      </p>
    </>;
}
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const currentYear = new Date().getFullYear();
  return <>
      <footer className="w-full bg-[#2D3436] py-8" data-id="element-174">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-175">
          <p className="text-white/40 text-xs mb-6 leading-relaxed" data-id="element-176">
            Aviso legal: Los resultados pueden variar de persona a persona. Este
            producto es informativo y educativo. No pretende diagnosticar,
            tratar, curar o prevenir ninguna enfermedad. Consulta con un
            profesional de la salud antes de comenzar cualquier programa de
            bienestar.
          </p>

          {/* Legal links */}
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-177">
            <button onClick={() => setOpenModal('privacy')} className="text-white/50 text-sm hover:text-[#C4A962] transition-colors flex items-center gap-1.5" data-id="element-178">
              <ShieldIcon className="w-3.5 h-3.5" data-id="element-179" />
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-180">|</span>
            <button onClick={() => setOpenModal('terms')} className="text-white/50 text-sm hover:text-[#C4A962] transition-colors flex items-center gap-1.5" data-id="element-181">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-182" />
              Términos y Condiciones
            </button>
          </div>

          <p className="text-white/60 text-sm" data-id="element-183">
            © {currentYear} Protocolo Vientre Plano Post-30. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence data-id="element-184">
        {openModal && <LegalModal type={openModal} onClose={() => setOpenModal(null)} data-id="element-185" />}
      </AnimatePresence>
    </>;
}